import { Injectable } from '@angular/core';
import { lastValueFrom, map, Observable } from 'rxjs';
import {
  ElectionClient,
  ElectionType,
  FvEvFaElectionOverviewDto,
  MapAreaDto,
  SideNavigationLinkDto
} from "@shared/clients/api.generated";
import { TranslateService } from "@ngx-translate/core";
import { ReferendumStatus } from "@shared/components/cards/referendum-status/referendum-status.model";
import { getMapAreaStyle, MapAreaData, MapAreasMapping } from "@shared/components/map/map-styles.model";
import { SidenavigationLink } from '@shared/components/sidenavigation/sidenavigation.model';
import { ExtendedElectionDto } from "./election-overview.model";

@Injectable({
  providedIn: 'root',
})
export class ElectionOverviewService {

  private readonly OldestYearForDisplayingMap = 2007;
  public electionMigrationCutOffDate = 2024;

  constructor(private readonly translateService: TranslateService,
    private readonly electionClient: ElectionClient,) { }

  isMigratedElection(electionDate: Date) {
    return electionDate.getFullYear() > this.electionMigrationCutOffDate;
  }

  getElectionOverviewData(overviewData$: Observable<FvEvFaElectionOverviewDto>) {
    return overviewData$.pipe(
      map((result: FvEvFaElectionOverviewDto) => {
        const resultType = result.resultType;
        const candidateDataLoadState = result.candidateDataLoadState;
        const voteCountStatus = result.voteCountStatusDto;
        const countStatusUpdated = voteCountStatus?.recentUpdateTimestamp;
        const electoralCoalitions = result.electoralCoalitionDtos;
        const resultSummary = result.resultSummaryDto;
        const countStatus = [
          {
            name: this.translateService.instant("election_overview.vote_percentage"),
            percentage: voteCountStatus.eligibleVoterPercentage
          },
          {
            name: "count_status.voting_prec_count_percentage",
            percentage: voteCountStatus.preCountPercentage
          },
          {
            name: "count_status.voting_fine_count_percentage",
            percentage: voteCountStatus.fineCountPercentage
          }
        ];
        const latestUpdates = result.recentUpdatedNominationDistrictDtoList.map(x => {
          return {
            id: x.id,
            district: x.nominationDistrictName,
            datetime: x.lastUpdate
          }
        }) || [];

        const partyStatus = result.partyResultDtoList?.map(x => {
          return {
            name: x.partyName,
            percentage: x.votingPercentage
          }
        }) || [];



        const highestVotesTopCandidate = result.candidateResultListDtoList && result.candidateResultListDtoList.length > 0
          ? result.candidateResultListDtoList[0].numberOfVotes : null;
        const topCandidates = result.candidateResultListDtoList?.map(x => {
          return {
            name: x.displayName,
            votes: x.numberOfVotes,
            percentage: highestVotesTopCandidate && highestVotesTopCandidate !== 0 ? (x.numberOfVotes! / highestVotesTopCandidate) * 100 : 0
          }
        }) || [];

        const referendumStatus = result.faElectionOverviewDto ? [
          {
            option: this.translateService.instant("simple_text.yes"),
            votes: result.faElectionOverviewDto?.yesVotes,
            percentage: result.faElectionOverviewDto?.yesPercentage
          },
          {
            option: this.translateService.instant("simple_text.no"),
            votes: result.faElectionOverviewDto?.noVotes,
            percentage: result.faElectionOverviewDto?.noPercentage
          }
        ] as ReferendumStatus[] : [];

        const mapAreaMapping = this.toMapAreaMapping(result.mapAreaDtoList);

        return {
          countStatus,
          resultType,
          countStatusUpdated,
          latestUpdates,
          partyStatus,
          topCandidates,
          referendumStatus,
          mapAreaMapping,
          electoralCoalitions,
          candidateDataLoadState,
          resultSummary
        };
      })
    );
  }

  toMapAreaMapping(mapAreaDtoList: MapAreaDto[]) {
    const mapAreaMapping: MapAreasMapping = {}
    mapAreaDtoList.forEach(x => {
      mapAreaMapping[x.code] = {
        displayName: x.displayName,
        style: getMapAreaStyle(x.resultType),
        id: x.id
      } as MapAreaData
    });
    return mapAreaMapping;
  }

  async getCurrentElection(): Promise<ExtendedElectionDto> {
    const election = await lastValueFrom(this.electionClient.getCurrentElection());
    const electionYear = this.getElectionYear(election.electionDate.toString());

    return {
      id: election.id,
      electionName: election.electionName,
      electionDate: election.electionDate,
      electionType: election.electionType,
      title: election.electionType === ElectionType.Referendum ? election.referendumTitle : undefined,
      isDisplayingMap: this.isDisplayingMap(electionYear),
      isDisplayingCards: this.isDisplayingCards(electionYear),
      electionYear: electionYear,
    }
  }

  mapConstituencies(constituencies: SideNavigationLinkDto[]): SidenavigationLink[] {
    return constituencies.map(constituency => {
      return {
        title: constituency.title,
        routerLink: "/",
        showChildren: true,
        navigationType: constituency.sideNaviationLinkType,
        children: constituency.children.map(
          nd => ({
            title: nd.title,
            routerLink: this.getNominationDistrictLink(nd.id),
            navigationType: nd.sideNaviationLinkType,
            showChildren: false,
            children: nd.children.map(ea => ({
              title: ea.title,
              routerLink: `${this.getNominationDistrictLink(nd.id)}/${this.getElectionAreaLink(ea.id)}`,
              navigationType: ea.sideNaviationLinkType,
              showChildren: false
            }))
          })
        )
      }
    });
  }

  mapNominationDistrict(municipalities: SideNavigationLinkDto[], nominationDistrictId: string): SidenavigationLink[] {
    return municipalities.map(x => {
      return {
        title: x.title,
        routerLink: this.getNominationDistrictLink(x.id),
        showChildren: true,
        navigationType: x.sideNaviationLinkType,
        children: x.children.map(ea => ({
          title: ea.title,
          routerLink: `${this.getNominationDistrictLink(nominationDistrictId)}/${this.getElectionAreaLink(ea.id)}`,
          navigationType: ea.sideNaviationLinkType,
          showChildren: false
        }))
      }
    });
  }

  mapMunicipalSideNavigation(items: SideNavigationLinkDto[], election?: ExtendedElectionDto): SidenavigationLink[] {
    if(election?.electionYear == 2001 || election?.electionYear == 2005 || election?.electionYear == 2009){
      return items.map(item => {
        return {
          title: this.translateService.instant('election_overview.navigation.municipal_item', { 'municipality': item.title }),
          navigationType: item.sideNaviationLinkType,
          routerLink: this.getMunicipalityLink(item.id),
          showChildren: false,
          children: item.children?.map(ea => {
            return {
              title: ea.title,
              routerLink: `${this.getMunicipalityLink(item.id)}/${this.getElectionAreaLink(ea.id)}`,
              navigationType: ea.sideNaviationLinkType
            }
          }),
        }
      });
    }
    return items.map(item => {
      return {
        title: item.title,
        routerLink: "/",
        navigationType: item.sideNaviationLinkType,
        showChildren: true,
        children: item.children?.map(municipality => {
          return {
            title: this.translateService.instant('election_overview.navigation.municipal_item', { 'municipality': municipality.title }),
            navigationType: municipality.sideNaviationLinkType,
            routerLink: this.getMunicipalityLink(municipality.id),
            showChildren: false,
            children: municipality.children?.map(ea => {
              return {
                title: ea.title,
                routerLink: `${this.getMunicipalityLink(municipality.id)}/${this.getElectionAreaLink(ea.id)}`,
                navigationType: ea.sideNaviationLinkType
              }
            }),
          }
        })
      }
    });
  }

  mapRegionalSideNavigation(items: SideNavigationLinkDto[]): SidenavigationLink[] {
    return items.map(item => {
      return {
        title: item.title,
        routerLink: this.getRegionLink(item.id),
        navigationType: item.sideNaviationLinkType,
        showChildren: false,
        children: item.children?.map(m => {
          return {
            title: this.translateService.instant('election_overview.navigation.municipal_item', { 'municipality': m.title }),
            navigationType: m.sideNaviationLinkType,
            routerLink: `${this.getRegionLink(item.id)}/${this.getMunicipalityLink(m.id)}`,
            showChildren: false,
            children: m.children?.map(ea => {
              return {
                title: ea.title,
                routerLink: `${this.getRegionLink(item.id)}/${this.getMunicipalityLink(m.id)}/${this.getElectionAreaLink(ea.id)}`,
                navigationType: ea.sideNaviationLinkType
              }
            }),
          }
        })
      }
    });
  }

  mapRegion(municipalities: SideNavigationLinkDto[], regionId: string): SidenavigationLink[] {
    return municipalities.map(x => {
      return {
        title: this.translateService.instant('election_overview.navigation.municipal_item', { 'municipality': x.title }),
        routerLink: `${this.getRegionLink(regionId)}/${this.getMunicipalityLink(x.id)}`,
        showChildren: false,
        navigationType: x.sideNaviationLinkType
      }
    });
  }

  mapMunicipality(electionAreas: SideNavigationLinkDto[], municipalityId: string): SidenavigationLink[] {
    return electionAreas.map(x => {
      return {
        title: x.title,
        routerLink: `${this.getMunicipalityLink(municipalityId)}/${this.getElectionAreaLink(x.id)}`,
        showChildren: false,
        navigationType: x.sideNaviationLinkType
      }
    });
  }

  mapRegionalMunicipality(electionAreas: SideNavigationLinkDto[], regionId: string, municipalityId: string): SidenavigationLink[] {
    return electionAreas.map(x => {
      return {
        title: x.title,
        routerLink: `${this.getRegionLink(regionId)}/${this.getMunicipalityLink(municipalityId)}/${this.getElectionAreaLink(x.id)}`,
        showChildren: false,
        navigationType: x.sideNaviationLinkType
      }
    });
  }

  getNominationDistrictLink(id: string) {
    return `nomination-district/${id}`;
  }

  private getElectionAreaLink(id: string) {
    return "polling-district/" + id;
  }

  private getRegionLink(id: string) {
    return `region/${id}`;
  }

  private getMunicipalityLink(id: string) {
    return `municipality/${id}`;
  }

  private getElectionYear(electionDate: string): number {
    const date = new Date(electionDate);
    return date.getFullYear();
  }

  private isDisplayingMap(electionYear: number): boolean {
    return electionYear > this.OldestYearForDisplayingMap;
  }
  
  private isDisplayingCards(electionYear: number): boolean {
    return (electionYear > this.OldestYearForDisplayingMap);
  }
}
